import { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for managing favorites functionality
 * Centralizes favorites state and logic to avoid duplication across pages
 * 
 * @param {string} itemType - Type of item: "news", "tender", "project", "company"
 * @param {string} token - Auth token for API calls
 * @returns {Object} - { favorites, showFavoritesOnly, setShowFavoritesOnly, isFavorite, handleFavoriteToggle, fetchFavorites }
 */
export default function useFavorites(itemType, token) {
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch user's favorites for this item type
  const fetchFavorites = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/favorites?item_type=${itemType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle different response structures
        const favs = data.favorites?.[itemType] || data.favorites || [];
        setFavorites(Array.isArray(favs) ? favs : []);
      }
    } catch (err) {
      console.error(`Error fetching ${itemType} favorites:`, err);
    } finally {
      setLoading(false);
    }
  }, [itemType, token]);

  // Fetch favorites when token changes
  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Check if an item is a favorite
  const isFavorite = useCallback((itemId) => {
    return favorites.includes(itemId);
  }, [favorites]);

  // Handle favorite toggle - updates local state
  const handleFavoriteToggle = useCallback((itemId, isFav) => {
    if (isFav) {
      setFavorites(prev => [...prev, itemId]);
    } else {
      setFavorites(prev => prev.filter(id => id !== itemId));
    }
  }, []);

  // Filter items to only show favorites
  const filterByFavorites = useCallback((items) => {
    if (!items || !Array.isArray(items)) return [];
    if (!showFavoritesOnly) return items;
    return items.filter(item => favorites.includes(item._id));
  }, [showFavoritesOnly, favorites]);

  return {
    favorites,
    showFavoritesOnly,
    setShowFavoritesOnly,
    isFavorite,
    handleFavoriteToggle,
    filterByFavorites,
    fetchFavorites,
    loading
  };
}
